import { TooltipProvider } from "@/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import React from "react";
import { BrowserRouter, Navigate, Outlet, Route, Routes, useLocation, useParams, useSearchParams } from "react-router";
import { appPath, appSettingsPath } from "./lib/appPaths";
import { FEATURE_FACTORIES } from "./lib/experimentalFeatures";
import { usePersistOrganizationLastLocation } from "./hooks/usePersistOrganizationLastLocation";
import { UserNotificationsListener } from "./hooks/useUserNotificationsWebsocket";
import { resolveOrganizationUidRedirect } from "./lib/organizationPath";
import { isReservedAppPathSegment } from "./lib/reservedAppPaths";
import { useConsumeIntegrationSetupReturnOnArrival } from "./hooks/useConsumeIntegrationSetupReturnOnArrival";
import { useOrganization } from "./hooks/useOrganizationData";
import { useRedirectIntegrationSetupReturn } from "./hooks/useRedirectIntegrationSetupReturn";
import { Toaster } from "sonner";
import "./App.css";

// Import pages
import AuthGuard from "./components/AuthGuard";
import { SetupGuard } from "./components/SetupGuard";
import { GlobalCommandPalette } from "./components/GlobalCommandPalette";
import { RequireExperimentalFeature } from "./components/RequireExperimentalFeature";
import { AccountProvider } from "./contexts/AccountProvider";
import { ThemeProvider } from "./contexts/ThemeProvider";
import { useAccount } from "./contexts/useAccount";
import { PermissionsProvider } from "./contexts/PermissionsProvider";
import { RequirePermission } from "./components/PermissionGate";
import { agentSectionRoute } from "./pages/factories/pages/settings/agentSectionRoutes";
import { isFactoryAppConfigureMode } from "./pages/factories/lib/factoryAppCanvasCopy";
import { Login } from "./pages/auth/Login";
import { OrganizationOnboardingRedirect } from "./pages/auth/OrganizationOnboardingRedirect";
import OwnerSetup from "./pages/auth/OwnerSetup";
import { RootOrganizationRedirect } from "./pages/auth/RootOrganizationRedirect";
import WelcomeRedirect from "./pages/auth/WelcomeRedirect";
import { CanvasSettingsPage } from "./pages/canvas/settings";
import {
  AutomationsPage,
  CreateWorkOrderComposeRedirect,
  FactoriesIndexPage,
  FactoryAppCanvasPage,
  FactoryAppSplitRunPage,
  FactoryHomeRedirect,
  FactoryLineEditPage,
  FactorySettingsLayout,
  LegacyFactoryAppRedirect,
  LegacyFactoryAppSplitRunRedirect,
  LegacyWorkOrderDetailRedirect,
  LegacyWorkOrderPermalinkRedirect,
  LegacyWorkOrdersRedirect,
  MissionsPage,
  NewWorkspacePage,
  OnboardingGate,
  OnboardingPage,
  VelocityPage,
  WikiPage,
  WorkOrdersPage,
  WorkspaceOverviewPage,
  ChecksPRFeedbackSetupPage,
  DatadogIntakeSetupPage,
  DependabotIntakeSetupPage,
  DiscussionPRFeedbackSetupPage,
  GitHubIntakeSetupPage,
  JiraIntakeSetupPage,
  LinearIntakeSetupPage,
  ProductiveIntakeSetupPage,
  PlanningSetupPage,
  RiskScoreSetupPage,
  SentryIntakeSetupPage,
} from "./pages/factories";
import { createFactoryLinePath, editFactoryLinePath } from "./pages/factories/lib/factoryPagePaths";
import { isPublicFactoryLinePath } from "./pages/factories/lib/publicFactoryLinePath";
import { FactoryLineAccessGate } from "./pages/factories/pages/FactoryLineAccessGate";
import {
  FactoryMoreRouteSwitch,
  FactoryWorkspaceLayoutSwitch,
  WorkOrderDetailRouteSwitch,
} from "./pages/factories/mobile/MobileShellSwitch";
import { WorkspaceLoadingProvider } from "./pages/factories/layout/workspaceLoading";
import { OnboardingEntryPathProvider } from "./pages/factories/pages/onboarding/OnboardingEntryPathProvider";
import { InitialWorkspaceOnboarding } from "./pages/factories/pages/onboarding/InitialWorkspaceOnboarding";
import { OnboardingWorkspaceResolutionProvider } from "./pages/factories/pages/onboarding/OnboardingWorkspaceResolutionProvider";
import {
  LegacyFactoryOrganizationSettingsRedirect,
  LegacyOrganizationSettingsRedirect,
} from "./pages/factories/pages/settings/FactorySettingsRedirects";
import { factorySettingsSectionRoutes } from "./pages/factories/pages/settings/factorySettingsSectionRoutes";
import { HomePage } from "./pages/home";
import { NewAppPage } from "./pages/home/NewAppPage";
import { GitHubInstallApprovedPage } from "./pages/github/GitHubInstallApprovedPage";
import { OrganizationSettings } from "./pages/organization/settings";
import { AppDefaultTabGate } from "./pages/app/AppDefaultTabGate";
import { isNotFoundError } from "./pages/app/workflowPageHelpers";
import InviteLinkAccept from "./pages/auth/InviteLinkAccept";
import AdminLayout from "./pages/admin/AdminLayout";
import OrganizationsListAdmin from "./pages/admin/OrganizationsList";
import OrganizationDetailAdmin from "./pages/admin/OrganizationDetail";
import AccountsListAdmin from "./pages/admin/AccountsList";
import InstallationSettingsAdmin from "./pages/admin/InstallationSettings";
import { InstallationEncryption as InstallationEncryptionAdmin } from "./pages/admin/InstallationEncryption";
import InstallationLicenseAdmin from "./pages/admin/InstallationLicense";
import RunnerTasksAdmin from "./pages/admin/RunnerTasks";
import { FleetManagerSetup as FleetManagerSetupAdmin } from "./pages/admin/FleetManagerSetup";
import { PolarWebhooks as PolarWebhooksAdmin } from "./pages/admin/PolarWebhooks";
import { Webhooks as WebhooksAdmin } from "./pages/admin/Webhooks";
import { PriceBooks as PriceBooksAdmin } from "./pages/admin/PriceBooks";
import ImpersonationBanner from "./components/ImpersonationBanner";
import LicenseExpiryBanner from "./components/LicenseExpiryBanner";
import { usePageObservability } from "./hooks/usePageObservability";

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes
    },
  },
});

const withAuthOnly = (Component: React.ComponentType) => (
  <AuthGuard>
    <Component />
  </AuthGuard>
);

const withAuthAndPermission = (Component: React.ComponentType, resource: string, action: string) => (
  <AuthGuard>
    <RequirePermission resource={resource} action={action}>
      <Component />
    </RequirePermission>
  </AuthGuard>
);

const withAuthPermissionAndFactoriesFeature = (Component: React.ComponentType, resource: string, action: string) => (
  <WorkspaceLoadingProvider>
    <AuthGuard>
      <RequirePermission resource={resource} action={action}>
        <RequireExperimentalFeature featureId={FEATURE_FACTORIES}>
          <Component />
        </RequireExperimentalFeature>
      </RequirePermission>
    </AuthGuard>
  </WorkspaceLoadingProvider>
);

function organizationScopedRouteTree() {
  return (
    <Route path=":organizationId" element={<OrganizationScope />}>
      <Route index element={withAuthAndPermission(HomePage, "canvases", "read")} />
      <Route path="apps">
        <Route path="new" element={withAuthAndPermission(NewAppPage, "canvases", "create")} />
        <Route path=":appId/settings" element={withAuthAndPermission(CanvasSettingsPage, "canvases", "update")} />
        <Route path=":appId" element={withAuthAndPermission(CanvasPageConfigureGate, "canvases", "read")} />
      </Route>
      <Route path="canvases/:canvasId/settings" element={<LegacyCanvasRedirect settings />} />
      <Route path="canvases/:canvasId" element={<LegacyCanvasRedirect />} />
      <Route path="workspaces">
        <Route index element={withAuthPermissionAndFactoriesFeature(FactoriesIndexPage, "factories", "read")} />
        <Route path="new" element={withAuthPermissionAndFactoriesFeature(NewWorkspacePage, "factories", "create")} />
        <Route
          path=":factoryKey"
          element={withAuthPermissionAndFactoriesFeature(FactoryWorkspaceLayoutSwitch, "factories", "read")}
        >
          <Route element={<OnboardingGate />}>
            <Route index element={<FactoryHomeRedirect />} />
            <Route path="setup" element={<OnboardingPage />} />
            <Route path="onboarding" element={<Navigate to="../setup" replace />} />
            <Route path="overview" element={<WorkspaceOverviewPage />} />
            <Route path="missions" element={<MissionsPage />} />
            <Route path="wiki" element={<WikiPage />} />
            <Route path="velocity" element={<VelocityPage />} />
            {agentSectionRoute}
            <Route path="more" element={<FactoryMoreRouteSwitch />} />
            <Route path="tasks">
              <Route index element={<WorkOrdersPage />} />
              <Route path="new" element={<CreateWorkOrderComposeGate />} />
              <Route path=":orderId" element={<LegacyWorkOrderDetailRedirect />} />
            </Route>
            <Route path="task/:orderNumber" element={<WorkOrderDetailRouteSwitch />} />
            {/* Back-compat for bookmarks made before `work-order(s)` was renamed to `task(s)`. */}
            <Route path="work-orders/*" element={<LegacyWorkOrdersRedirect />} />
            <Route path="work-order/:orderNumber" element={<LegacyWorkOrderPermalinkRedirect />} />
            <Route path="lines">
              <Route index element={<FactoryHomeRedirect />} />
              <Route path="new" element={<FactoryLineEditPageGate />} />
              <Route path=":lineId/edit" element={<FactoryLineEditPageGate />} />
              <Route path=":lineId/setup/comments" element={<DiscussionPRFeedbackSetupPage />} />
              <Route path=":lineId/setup/checks" element={<ChecksPRFeedbackSetupPage />} />
              <Route path=":lineId/setup/risk-score" element={<RiskScoreSetupPage />} />
              <Route path=":lineId/setup/planning" element={<PlanningSetupPage />} />
              <Route path=":lineId/setup/github" element={<GitHubIntakeSetupPage />} />
              <Route path=":lineId/setup/sentry" element={<SentryIntakeSetupPage />} />
              <Route path=":lineId/setup/datadog" element={<DatadogIntakeSetupPage />} />
              <Route path=":lineId/setup/dependabot" element={<DependabotIntakeSetupPage />} />
              <Route path=":lineId/setup/jira" element={<JiraIntakeSetupPage />} />
              <Route path=":lineId/setup/linear" element={<LinearIntakeSetupPage />} />
              <Route path=":lineId/setup/productive" element={<ProductiveIntakeSetupPage />} />
            </Route>
            <Route path="automations">
              <Route index element={<AutomationsPage />} />
              <Route path="new" element={<LegacyAutomationsNewLineRedirect />} />
              <Route path=":lineId/edit" element={<LegacyAutomationsLineEditRedirect />} />
              <Route path=":appId" element={<FactoryCanvasConfigureGate />} />
              <Route path=":appId/split-run" element={<FactoryAppSplitRunPage />} />
            </Route>
            <Route path="apps/:appId" element={<LegacyFactoryAppRedirect />} />
            <Route path="apps/:appId/split-run" element={<LegacyFactoryAppSplitRunRedirect />} />
          </Route>
        </Route>
        <Route path=":factoryKey/lines/:lineId" element={<FactoryLineAccessGate />} />
        <Route
          path=":factoryKey/settings"
          element={withAuthPermissionAndFactoriesFeature(FactorySettingsLayout, "factories", "read")}
        >
          {factorySettingsSectionRoutes}
        </Route>
        <Route
          path=":factoryKey/organization/*"
          element={withAuthPermissionAndFactoriesFeature(
            LegacyFactoryOrganizationSettingsRedirect,
            "factories",
            "read",
          )}
        />
      </Route>
      <Route
        path="organization/*"
        element={withAuthPermissionAndFactoriesFeature(LegacyOrganizationSettingsRedirect, "factories", "read")}
      />
      <Route
        path="settings/llm-spend"
        element={withAuthOnly(() => (
          <LegacyOrganizationSettingsRedirect destination="llm-spend" />
        ))}
      />
      <Route path="settings/*" element={withAuthOnly(OrganizationSettings)} />
    </Route>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <AccountProvider>
          <TooltipProvider delayDuration={150}>
            <AppRouter />
          </TooltipProvider>
          <Toaster position="bottom-center" closeButton />
        </AccountProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

function AppRouter() {
  return (
    <BrowserRouter>
      <PageObservabilityScope />
      <div className="flex h-dvh flex-col overflow-hidden">
        <ImpersonationBanner />
        <LicenseExpiryBanner />
        {/* Phone shells read this region so a fixed shell stays below app banners. */}
        <div className="relative flex-1 overflow-auto" data-app-content-region="">
          <SetupGuard>
            <GlobalCommandPalette />
            <Routes>
              <Route path="login" element={<Login />} />
              <Route path="signup" element={<Login mode="signup" />} />
              <Route path="welcome" element={withAuthOnly(WelcomeRedirect)} />
              <Route path="onboarding" element={withAuthOnly(OrganizationOnboardingRoute)} />
              <Route path="setup" element={<OwnerSetup />} />
              <Route path="admin" element={<AdminLayout />}>
                <Route index element={<OrganizationsListAdmin />} />
                <Route path="accounts" element={<AccountsListAdmin />} />
                <Route path="settings" element={<InstallationSettingsAdmin />} />
                <Route path="encryption" element={<InstallationEncryptionAdmin />} />
                <Route path="license" element={<InstallationLicenseAdmin />} />
                <Route path="price-books" element={<PriceBooksAdmin />} />
                <Route path="runner-tasks" element={<RunnerTasksAdmin />} />
                <Route path="fleet-manager-setup" element={<FleetManagerSetupAdmin />} />
                <Route path="polar-webhooks" element={<PolarWebhooksAdmin />} />
                <Route path="webhooks" element={<WebhooksAdmin />} />
                <Route path="sentry-webhooks" element={<Navigate to="/admin/webhooks" replace />} />
                <Route path="organizations/:orgId" element={<OrganizationDetailAdmin />} />
              </Route>
              <Route path="" element={withAuthOnly(RootOrganizationRedirect)} />
              <Route path="invite/:token" element={withAuthOnly(InviteLinkAccept)} />
              {/* GitHub App owners who approve an install request may not have a SuperPlane session. */}
              <Route path="github/approved" element={<GitHubInstallApprovedPage />} />
              {organizationScopedRouteTree()}
              <Route path="*" element={<Navigate to="/" />} />
            </Routes>
          </SetupGuard>
        </div>
      </div>
    </BrowserRouter>
  );
}

function OrganizationOnboardingRoute() {
  return (
    <OrganizationOnboardingRedirect
      renderWorkspace={(workspace, entryPath, reresolveWorkspace) => (
        <OnboardingEntryPathProvider path={entryPath}>
          <OnboardingWorkspaceResolutionProvider resolve={reresolveWorkspace}>
            <InitialWorkspaceOnboarding
              organizationId={workspace.organizationSlug}
              factoryKey={workspace.workspaceKey}
            />
          </OnboardingWorkspaceResolutionProvider>
        </OnboardingEntryPathProvider>
      )}
    />
  );
}

function PageObservabilityScope() {
  usePageObservability();
  return null;
}

export function OrganizationScope() {
  const { organizationId: segment } = useParams<{ organizationId: string }>();
  const { account, loading: accountLoading } = useAccount();
  const accountId = account?.id;
  const location = useLocation();

  const isReserved = isReservedAppPathSegment(segment);
  const publicLine = isPublicFactoryLinePath(location.pathname);
  const guestLine = publicLine && (accountLoading || !account);
  // The route param accepts either the org slug or its UID, so resolve it
  // once here and self-correct any UID URL to the slug below. Every other
  // in-app link reuses this same `:organizationId` URL segment, so fixing
  // it at this single boundary keeps the rest of the app slug-only.
  const {
    data: organization,
    isLoading: organizationLoading,
    error: organizationError,
  } = useOrganization(segment ?? "", !isReserved && !!segment && !guestLine);
  const resolvedId = organization?.metadata?.id ?? "";
  const resolvedSlug = organization?.metadata?.slug ?? "";
  useRedirectIntegrationSetupReturn(segment, resolvedSlug);
  useConsumeIntegrationSetupReturnOnArrival(resolvedSlug || segment);

  const uidRedirectPath =
    !isReserved && segment
      ? resolveOrganizationUidRedirect({
          pathname: location.pathname,
          search: location.search,
          hash: location.hash,
          segment,
          organizationId: resolvedId,
          organizationSlug: resolvedSlug,
        })
      : null;
  usePersistOrganizationLastLocation({
    accountId,
    resolvedSlug,
    isReserved,
    uidRedirectPath,
    path: `${location.pathname}${location.search}`,
  });

  if (isReserved) {
    return <Navigate to="/" replace />;
  }

  if (uidRedirectPath) {
    return <Navigate to={uidRedirectPath} replace />;
  }

  if (guestLine) {
    return <Outlet />;
  }

  if (organizationLoading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <p className="text-gray-500 dark:text-gray-400">Loading organization...</p>
      </div>
    );
  }

  if (isNotFoundError(organizationError) && !publicLine) {
    return (
      <div className="flex justify-center items-center h-screen">
        <p className="text-gray-500 dark:text-gray-400">Organization not found</p>
      </div>
    );
  }

  return (
    <PermissionsProvider>
      <UserNotificationsListener organizationId={resolvedId} />
      <Outlet />
    </PermissionsProvider>
  );
}

function CreateWorkOrderComposeGate() {
  return (
    <RequirePermission resource="work_orders" action="create">
      <CreateWorkOrderComposeRedirect />
    </RequirePermission>
  );
}

function FactoryLineEditPageGate() {
  return (
    <RequirePermission resource="factories" action="update">
      <FactoryLineEditPage />
    </RequirePermission>
  );
}

function CanvasConfigureGate({ children }: { children: React.ReactNode }) {
  const [searchParams] = useSearchParams();
  if (isFactoryAppConfigureMode(searchParams)) {
    return (
      <RequirePermission resource="canvases" action="update">
        {children}
      </RequirePermission>
    );
  }
  return <>{children}</>;
}

function CanvasPageConfigureGate() {
  return (
    <CanvasConfigureGate>
      <AppDefaultTabGate />
    </CanvasConfigureGate>
  );
}

function FactoryCanvasConfigureGate() {
  return (
    <CanvasConfigureGate>
      <FactoryAppCanvasPage />
    </CanvasConfigureGate>
  );
}

function LegacyAutomationsNewLineRedirect() {
  const { organizationId, factoryKey } = useParams<{ organizationId: string; factoryKey: string }>();
  if (!organizationId || !factoryKey) return <Navigate to="/" replace />;
  return <Navigate to={createFactoryLinePath(organizationId, factoryKey)} replace />;
}

function LegacyAutomationsLineEditRedirect() {
  const { organizationId, factoryKey, lineId } = useParams<{
    organizationId: string;
    factoryKey: string;
    lineId: string;
  }>();
  if (!organizationId || !factoryKey || !lineId) return <Navigate to="/" replace />;
  return <Navigate to={editFactoryLinePath(organizationId, factoryKey, lineId)} replace />;
}

function LegacyCanvasRedirect({ settings = false }: { settings?: boolean }) {
  const { organizationId, canvasId } = useParams<{ organizationId: string; canvasId: string }>();
  const location = useLocation();

  if (!organizationId || !canvasId) return <Navigate to="/" replace />;

  const path = settings ? appSettingsPath(organizationId, canvasId) : appPath(organizationId, canvasId);
  return <Navigate to={`${path}${location.search}`} replace />;
}

export default App;
